import { call, delay, put, select, takeEvery, takeLatest, throttle } from "redux-saga/effects";
import {
  updateControllers,
  updateProductBatch,
  updateProductTs,
  setControllerReconnectPromptVisible,
  setLatestTestInteractionDate,
} from "@actions/productActions";
import {
  selectTestStatus,
  selectTestId,
  selectIsTestCompleted,
  selectShouldWaitForNextEyeOnFinish,
} from "@store/tests/testSelectors";
import { setTestStatus } from "@store/tests/testSlice";
import { checkTestExists } from "@utils/api-communication/RestApiCli";
import { rebindSocketToSelectedDevice } from "@utils/api-communication/WebSocketCli";
import { sendReconnectControllersMessage } from "@utils/api-communication/WsUtil";
import { TestStatus } from "types/testTypes";
import { TIMEOUTS } from "@constants/timeoutConstants";
import { REQUEST_CONTROLLER_RECONNECT, UPDATE_SCENE } from "@utils/redux-utils/constants";
import {
  selectControllerConnectionStatus,
  selectIsVRInTestMode,
  selectLastTestInteractionDate,
} from "@utils/redux-utils/selectors/productSelectors";
import { ControllerConnectionState } from "types/productTypes";
import { PayloadAction } from "@reduxjs/toolkit";

const checkTestExistsPromise = (queryMap: { uuid: string }) => {
  return new Promise((resolve, reject) => {
    checkTestExists(
      queryMap,
      (response) => resolve(response),
      (error) => reject(error),
      (unauth) => reject({ unauthorized: true, ...unauth }),
    );
  });
};

function* handleUpdateProductTs() {
  try {
    const testStatus = yield select(selectTestStatus);
    const testId = yield select(selectTestId);
    const isCompleted = yield select(selectIsTestCompleted);
    const shouldWaitForNextEyeOnFinish = yield select(selectShouldWaitForNextEyeOnFinish);

    if (
      testId &&
      !isCompleted &&
      !shouldWaitForNextEyeOnFinish &&
      (testStatus === TestStatus.Started || testStatus === TestStatus.CompleteRequested)
    ) {
      const response = yield call(checkTestExistsPromise, { uuid: testId });

      if (response?.data?.exists) {
        yield put(setTestStatus({ status: TestStatus.Completed }));
      }
    }
  } catch (error) {
    console.error("Error checking test status:", error);
  }
}

function* handleControllerConnectionChange() {
  yield delay(0);
  const state: ControllerConnectionState | null = yield select(selectControllerConnectionStatus);

  const isDisconnected =
    state === ControllerConnectionState.Error || state === ControllerConnectionState.None;

  if (isDisconnected) {
    console.warn("Controller connection issue detected:", state);
  }
}

function* handleControllerReconnectRequest() {
  try {
    yield call(sendReconnectControllersMessage);
  } finally {
    yield put(setControllerReconnectPromptVisible(false));
  }
}

function* handleUpdateSceneSaga(action: PayloadAction<{ scene: string; lastInteraction: Date }>) {
  const { lastInteraction } = action.payload;
  const latestVRTestInteractionDate: Date | null = yield select(selectLastTestInteractionDate);
  const isVRInTestMode = yield select(selectIsVRInTestMode);
  if (isVRInTestMode || !latestVRTestInteractionDate) {
    yield put(setLatestTestInteractionDate(lastInteraction));
  }
}

const batchUpdatesField = (action: any, field: string) => {
  const payload = action?.payload;
  return Boolean(payload && typeof payload === "object" && field in payload);
};

export default function* productSagas() {
  yield throttle(TIMEOUTS.PRODUCT_TS_CHANGE_HANDLING, updateProductTs, handleUpdateProductTs);
  yield takeLatest(updateControllers, handleControllerConnectionChange);
  yield takeLatest(updateProductBatch, function* (action: any) {
    if (batchUpdatesField(action, "controllers")) {
      yield call(handleControllerConnectionChange);
    }
  });
  yield takeEvery(updateProductBatch, function* (action: any) {
    if (batchUpdatesField(action, "pid")) {
      yield call(rebindSocketToSelectedDevice);
    }
  });
  yield takeLatest(REQUEST_CONTROLLER_RECONNECT, handleControllerReconnectRequest);
  yield takeLatest(UPDATE_SCENE, handleUpdateSceneSaga);
}
